import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Check, X, Activity, AlertTriangle, FileText, ChevronDown, Cross, Plus } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma } from "../context/FirmaContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ExportButton } from "../components/ExportButton";
import { useSegnalazioni, SegnalazioneButton, SegnalazioniBadge } from "../components/Segnalazioni";
import { ConfirmUndoDialog } from "../components/ConfirmUndoDialog";
import { MeseGiorniEsplosi, RecapGiorno, AllertaBox } from "../components/MeseGiorniEsplosi";
import { UscitiInfoBox, isFuori, fuoriLabel, isDimesso } from "../components/UscitiInfoBox";
import { EsBadge } from "../components/EsBadge";
import { CorrettoBadge } from "../components/CorrettoBadge";
import { ElencoCorrezioniBtn } from "../components/ElencoCorrezioni";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const DOW = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const ymd = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`; };
const riskColor = { basso: "#15803d", medio: "#c2660c", alto: "#dc2626" };
const fmtEvac = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" }); } catch { return iso || ""; } };
const fmtRel = (iso) => {
  if (!iso) return "";
  try {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(iso + "T00:00:00");
    const diff = Math.round((today - d) / 86400000);
    if (diff === 0) return "oggi";
    if (diff === 1) return "ieri";
    return d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long" });
  } catch { return iso; }
};
const ALVO_COLOR = "#7c3aed";

export const CONSISTENZE = [
  { v: "normale", l: "Normale", c: "#15803d" },
  { v: "diarrea", l: "Diarrea", c: "#dc2626" },
  { v: "stipsi", l: "Stipsi (dura)", c: "#c2660c" },
  { v: "liquida", l: "Liquida", c: "#dc2626" },
  { v: "sangue", l: "Con sangue/muco", c: "#b91c1c" },
];
export const QUANTITA = [
  { v: "scarsa", l: "Scarsa" },
  { v: "normale", l: "Normale" },
  { v: "abbondante", l: "Abbondante" },
];
export const ALERT_CONS = ["diarrea", "liquida", "sangue"];
const consInfo = (v) => CONSISTENZE.find((c) => c.v === v);
const isAlert = (v) => ALERT_CONS.includes(v);
export const AIUTI = [
  { v: "clistere", l: "Clistere / Clisma" },
  { v: "microclisma", l: "Microclisma" },
  { v: "supposta", l: "Supposta (glicerina)" },
  { v: "lassativo", l: "Lassativo orale" },
  { v: "manovra", l: "Svuotamento manuale" },
  { v: "altro", l: "Altro" },
];
const aiutoInfo = (v) => AIUTI.find((a) => a.v === v);

function AiutoDialog({ ctx, onClose, onSave }) {
  const [tipo, setTipo] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (ctx) { setTipo(ctx.rec?.aiuto || ""); setNote(ctx.rec?.aiuto_note || ""); } }, [ctx]);
  const pill = (active) => `px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${active ? "text-white border-transparent" : "bg-secondary hover:bg-secondary/70 border-transparent text-foreground"}`;
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="alvo-aiuto-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Cross size={18} className="text-emerald-600" /> Aiuto sanitario</DialogTitle>
          <DialogDescription>{ctx?.nome} · {ctx ? fmtDate(ctx.data) : ""} — come è stato aiutato l'ospite</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold mb-1.5 block">Tipo di aiuto</label>
            <div className="flex flex-wrap gap-2" data-testid="alvo-aiuto-tipi">
              {AIUTI.map((a) => (
                <button key={a.v} onClick={() => setTipo(tipo === a.v ? "" : a.v)} className={pill(tipo === a.v)} style={tipo === a.v ? { background: "#059669" } : {}} data-testid={`alvo-aiuto-tipo-${a.v}`}>{a.l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">Note (facoltative)</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Dettagli dell'aiuto sanitario..." data-testid="alvo-aiuto-note" />
          </div>
        </div>
        <DialogFooter>
          {ctx?.rec?.aiuto && <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 mr-auto" onClick={() => onSave({ aiuto: "", aiuto_note: "" })} data-testid="alvo-aiuto-rimuovi">Rimuovi</Button>}
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => { if (!tipo) { toast.error("Seleziona il tipo di aiuto"); return; } onSave({ aiuto: tipo, aiuto_note: note || "" }); }} disabled={!tipo} className="bg-emerald-600 hover:bg-emerald-700" data-testid="alvo-aiuto-salva"><Check size={15} className="mr-1" /> Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AiutoBadge({ rec }) {
  if (!rec?.aiuto) return null;
  const fmtD = (ts) => { if (!ts) return ""; const d = new Date(ts); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-emerald-100 text-emerald-700" title={rec.aiuto_note || ""} data-testid="alvo-aiuto-badge">
      <Cross size={11} /> Aiuto: {aiutoInfo(rec.aiuto)?.l || rec.aiuto}{rec.aiuto_operatore ? ` · ✍️ ${rec.aiuto_operatore}` : ""}{rec.aiuto_ts ? ` · ${fmtD(rec.aiuto_ts)}` : ""}
    </span>
  );
}

// ---- Dialog dettagli (consistenza/quantità/note) condiviso ----
function DettagliDialog({ ctx, onClose, onSave }) {
  const [stato, setStato] = useState("si");
  const [consistenza, setConsistenza] = useState("");
  const [quantita, setQuantita] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (ctx) {
      setStato(ctx.rec?.stato || "si");
      setConsistenza(ctx.rec?.consistenza || "");
      setQuantita(ctx.rec?.quantita || "");
      setNote(ctx.rec?.note || "");
    }
  }, [ctx]);
  const pill = (active, color) => `px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${active ? "text-white border-transparent" : "bg-secondary hover:bg-secondary/70 border-transparent text-foreground"}`;
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="alvo-dettagli-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Activity size={18} style={{ color: ALVO_COLOR }} /> Dettagli evacuazione</DialogTitle>
          <DialogDescription>{ctx?.nome} · {ctx ? fmtDate(ctx.data) : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 block">Esito</label>
            <div className="flex gap-2">
              <button onClick={() => setStato("si")} className={pill(stato === "si")} style={stato === "si" ? { background: "#16a34a" } : {}} data-testid="alvo-dett-si"><Check size={14} className="inline mr-1" /> Sì</button>
              <button onClick={() => setStato("no")} className={pill(stato === "no")} style={stato === "no" ? { background: "#dc2626" } : {}} data-testid="alvo-dett-no"><X size={14} className="inline mr-1" /> No</button>
            </div>
          </div>
          {stato === "si" && (
            <>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 block">Consistenza / tipo</label>
                <div className="flex flex-wrap gap-2" data-testid="alvo-dett-consistenza">
                  {CONSISTENZE.map((c) => (
                    <button key={c.v} onClick={() => setConsistenza(consistenza === c.v ? "" : c.v)} className={pill(consistenza === c.v)} style={consistenza === c.v ? { background: c.c } : {}} data-testid={`alvo-dett-cons-${c.v}`}>{c.l}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 block">Quantità</label>
                <div className="flex flex-wrap gap-2" data-testid="alvo-dett-quantita">
                  {QUANTITA.map((c) => (
                    <button key={c.v} onClick={() => setQuantita(quantita === c.v ? "" : c.v)} className={pill(quantita === c.v)} style={quantita === c.v ? { background: ALVO_COLOR } : {}} data-testid={`alvo-dett-qta-${c.v}`}>{c.l}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 block">Note</label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Osservazioni particolari (es. dolore, sforzo, motivo mancata evacuazione...)" data-testid="alvo-dett-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="alvo-dett-annulla">Annulla</Button>
          <Button onClick={() => onSave({ stato, consistenza: consistenza || null, quantita: quantita || null, note: note || null })} data-testid="alvo-dett-salva"><Check size={15} className="mr-1" /> Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AlvoInfoDialog({ ctx, onClose, onEdit, canEdit }) {
  const rec = ctx?.rec;
  const info = consInfo(rec?.consistenza);
  const hasData = rec && (rec.consistenza || rec.quantita || rec.note || rec.stato);
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="alvo-info-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Activity size={18} style={{ color: ALVO_COLOR }} /> {ctx?.nome}</DialogTitle>
          <DialogDescription>{ctx ? fmtDate(ctx.data) : ""} — dettaglio evacuazione (sola lettura).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            {rec?.stato && <span className="rounded-full px-2.5 py-1 text-xs font-bold text-white" style={{ background: rec.stato === "si" ? "#16a34a" : "#dc2626" }}>Evacuazione: {rec.stato === "si" ? "Sì" : "No"}</span>}
            {info && <span className="rounded-full px-2.5 py-1 text-xs font-bold text-white" style={{ background: info.c }}>{info.l}</span>}
            {rec?.quantita && <span className="rounded-full px-2.5 py-1 text-xs font-semibold bg-secondary text-muted-foreground">Quantità: {QUANTITA.find((q) => q.v === rec.quantita)?.l}</span>}
            {rec?.operatore && <span className="rounded-full px-2.5 py-1 text-xs font-semibold bg-secondary text-muted-foreground">da {rec.operatore}</span>}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-0.5">Note</div>
            <p className="whitespace-pre-wrap" data-testid="alvo-info-note">{rec?.note || "—"}</p>
          </div>
          {!hasData && <p className="text-sm text-muted-foreground">Nessun dettaglio registrato per questo giorno.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="alvo-info-chiudi">Chiudi</Button>
          {canEdit && <Button onClick={onEdit} data-testid="alvo-info-modifica">{hasData ? "Modifica" : "Aggiungi dettagli"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsBadge({ rec, testid }) {
  if (!rec) return null;
  const info = consInfo(rec.consistenza);
  if (!info && !rec.quantita && !rec.note) return null;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" data-testid={testid}>
      {info && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: info.c + "1f", color: info.c }}>{isAlert(rec.consistenza) && <AlertTriangle size={11} />}{info.l}</span>}
      {rec.quantita && <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-secondary text-muted-foreground">{QUANTITA.find((q) => q.v === rec.quantita)?.l}</span>}
      {rec.note && <span title={rec.note}><FileText size={12} className="text-muted-foreground" /></span>}
    </span>
  );
}

export default function Alvo({ ospiteId, embedded }) {
  const { selectedStructure } = useAuth();
  const qc = useQueryClient();
  const today = ymd(new Date());

  const setCell = async (ospite_id, structure_id, day, stato, operatore, extra) => {
    try {
      const payload = { ospite_id, structure_id, data: day, stato };
      if (operatore) payload.operatore = operatore;
      if (extra) Object.assign(payload, extra);
      await api.post("/alvo/register", payload);
      qc.invalidateQueries({ queryKey: ["alvo"] });
      qc.invalidateQueries({ queryKey: ["alvo-day"] });
      qc.invalidateQueries({ queryKey: ["alvo-mese"] });
      qc.invalidateQueries({ queryKey: ["alvo-diarrea"] });
      qc.invalidateQueries({ queryKey: ["correzioni"] });
      if (stato === "no" && !extra) toast.info("Registrato. Verrà valutata l'allerta 3 giorni.");
    } catch { toast.error("Errore"); }
  };

  if (embedded) {
    return <AlvoGridEmbedded ospiteId={ospiteId} selectedStructure={selectedStructure} today={today} setCell={setCell} />;
  }
  return <AlvoEsploso selectedStructure={selectedStructure} today={today} setCell={setCell} />;
}

function AlvoEsploso({ selectedStructure, today, setCell }) {
  const [showDimessi, setShowDimessi] = useState(false);
  const { canSign, signerName, mustUnlock } = useFirma();
  const navigate = useNavigate();
  const [openDay, setOpenDay] = useState(today);
  const [mese, setMese] = useState(today.slice(0, 7));
  const [dett, setDett] = useState(null);
  const [info, setInfo] = useState(null);
  const [confirmUndo, setConfirmUndo] = useState(null);
  const [aiuto, setAiuto] = useState(null);
  const selDate = openDay || today;

  const { data: meseData } = useQuery({
    queryKey: ["alvo-mese", selectedStructure, mese],
    queryFn: async () => (await api.get("/alvo/mese", { params: { structure_id: selectedStructure, mese } })).data,
  });
  const { data } = useQuery({
    queryKey: ["alvo-day", selectedStructure, selDate],
    queryFn: async () => (await api.get("/alvo/day", { params: { structure_id: selectedStructure, data: selDate } })).data,
  });
  const { data: alvoAlert = [] } = useQuery({
    queryKey: ["alvo-alert", selectedStructure],
    queryFn: async () => (await api.get("/dashboard/alvo-alert", { params: { structure_id: selectedStructure } })).data,
  });
  const rows = data?.rows || [];
  const records = data?.records || {};
  const { byKey: segByKey, aperte: segAperte, refetch: segRefetch } = useSegnalazioni("alvo", selectedStructure, selDate);

  const onCell = (r, stato) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    const rec = records[r.ospite_id];
    if (rec?.stato === stato) { setConfirmUndo({ nome: `${r.cognome} ${r.nome}`, extra: stato === "si" ? "evacuazione Sì" : "evacuazione No", action: () => setCell(r.ospite_id, r.structure_id, selDate, "", signerName) }); return; }
    setCell(r.ospite_id, r.structure_id, selDate, stato, signerName);
  };

  const openDett = (r) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    const rec = records[r.ospite_id];
    setDett({ ospite_id: r.ospite_id, structure_id: r.structure_id, data: selDate, nome: `${r.cognome} ${r.nome}`, rec });
  };
  const openInfo = (r) => setInfo({ r, rec: records[r.ospite_id], data: selDate, nome: `${r.cognome} ${r.nome}` });
  const infoCanEdit = info && canSign;
  const saveDett = async (vals) => {
    await setCell(dett.ospite_id, dett.structure_id, dett.data, vals.stato, signerName, { consistenza: vals.consistenza, quantita: vals.quantita, note: vals.note });
    setDett(null);
    toast.success("Dettagli salvati");
  };

  const isDimessoTop = isDimesso;
  const openAiuto = (r) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    setAiuto({ ospite_id: r.ospite_id, structure_id: r.structure_id, data: selDate, nome: `${r.cognome} ${r.nome}`, rec: records[r.ospite_id] });
  };
  const saveAiuto = async (vals) => {
    const rec = records[aiuto.ospite_id];
    await setCell(aiuto.ospite_id, aiuto.structure_id, aiuto.data, rec?.stato || "", signerName, { aiuto: vals.aiuto, aiuto_note: vals.aiuto_note });
    setAiuto(null);
    toast.success(vals.aiuto ? "Aiuto sanitario registrato" : "Aiuto sanitario rimosso");
  };
  const attiviRows = rows.filter((r) => !isDimessoTop(r) && !(r.fuori_giorno ?? isFuori(r)));
  const opCountsDay = {};
  let siOggi = 0;
  attiviRows.forEach((r) => {
    const rec = records[r.ospite_id];
    if (rec?.stato === "si") siOggi++;
    if (rec?.stato === "si" || rec?.stato === "no") {
      const n = rec.operatore || "—"; opCountsDay[n] = (opCountsDay[n] || 0) + 1;
    }
  });
  const noOggi = Math.max(0, attiviRows.length - siOggi);
  const alvoOperatoriGiorno = Object.entries(opCountsDay).map(([nome, count]) => ({ nome, count })).sort((a, b) => b.count - a.count);
  const alvoStats = [
    { key: "si", label: "Sì (evacuazione)", value: siOggi, tone: "ok" },
    { key: "no", label: "No (default)", value: noOggi, tone: "muted" },
  ];

  const exportCols = [
    { header: "Ospite", accessor: (r) => `${r.cognome} ${r.nome}` },
    { header: "Rischio", accessor: "rischio" },
    { header: "Esito", accessor: (r) => { const s = records[r.ospite_id]?.stato; return s === "si" ? "Sì" : s === "no" ? "No" : ""; } },
    { header: "Consistenza", accessor: (r) => consInfo(records[r.ospite_id]?.consistenza)?.l || "" },
    { header: "Quantità", accessor: (r) => QUANTITA.find((q) => q.v === records[r.ospite_id]?.quantita)?.l || "" },
    { header: "Note", accessor: (r) => records[r.ospite_id]?.note || "" },
    { header: "Aiuto sanitario", accessor: (r) => { const a = records[r.ospite_id]; return a?.aiuto ? `${aiutoInfo(a.aiuto)?.l || a.aiuto}${a.aiuto_operatore ? ` (${a.aiuto_operatore})` : ""}` : ""; } },
  ];

  const consMese = meseData?.consistenze || {};
  const recap = (
    <div className="gr-card p-4 mb-4" data-testid="alvo-recap-mese">
      <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Activity size={14} style={{ color: ALVO_COLOR }} /> Recap del mese — evacuazioni</div>
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold bg-green-100 text-green-700" data-testid="alvo-recap-si">Sì <span className="bg-green-600 text-white rounded-full text-xs px-1.5 py-0.5 tabnum">{meseData?.si ?? 0}</span></span>
        <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold bg-red-100 text-red-700" data-testid="alvo-recap-no">No <span className="bg-red-600 text-white rounded-full text-xs px-1.5 py-0.5 tabnum">{meseData?.no ?? 0}</span></span>
        {CONSISTENZE.filter((c) => consMese[c.v]).map((c) => (
          <span key={c.v} className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold" style={{ background: c.c + "1f", color: c.c }} data-testid={`alvo-recap-cons-${c.v}`}>{isAlert(c.v) && <AlertTriangle size={12} />}{c.l} <span className="text-white rounded-full text-xs px-1.5 py-0.5 tabnum" style={{ background: c.c }}>{consMese[c.v]}</span></span>
        ))}
        {(meseData?.operatori || []).map((o) => (
          <span key={o.nome} className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold" style={{ background: ALVO_COLOR + "18", color: ALVO_COLOR }} data-testid={`alvo-recap-op-${o.nome}`}>{o.nome} <span className="text-white rounded-full text-xs px-1.5 py-0.5 tabnum" style={{ background: ALVO_COLOR }}>{o.count}</span></span>
        ))}
      </div>
    </div>
  );

  const alertBox = (
    <AllertaBox tone="bad" title="Alvo — nessuna evacuazione da 3+ giorni" icon={AlertTriangle}
      items={alvoAlert} testid="alvo-alert-box" onItemClick={(it) => navigate(`/ospiti/${it.ospite_id}?tab=alvo`)}
      renderItem={(it) => (<span><b>{it.cognome} {it.nome}</b> <span className="ml-1 text-red-700 font-bold">{it.giorni != null ? `${it.giorni}g` : "mai"}</span>{it.ultima ? <span className="text-muted-foreground"> · ultima {it.ultima}</span> : ""}</span>)} />
  );

  return (
    <MeseGiorniEsplosi
      title="Alvo intestinale" subtitle="Registro giornaliero delle evacuazioni · consulta e modifica anche i giorni passati (resta la firma dell'operatore)"
      Icon={Activity} color={ALVO_COLOR}
      mese={mese} onMese={setMese} openDay={openDay} onOpenDay={setOpenDay}
      counts={meseData?.counts || {}} countLabel="evacuazioni (Sì)"
      headerRight={<><SegnalazioniBadge categoria="alvo" aperte={segAperte} /><ElencoCorrezioniBtn categoria="alvo" structureId={selectedStructure} mese={mese} /></>}
      monthTop={<>{alertBox}{recap}</>}
      detailRight={<ExportButton filename="alvo" title={`Alvo intestinale · ${selDate}`} subtitle="" columns={exportCols} rows={rows} color={ALVO_COLOR} testid="alvo-export" />}
    >
      {alertBox}
      <RecapGiorno operatori={alvoOperatoriGiorno} stats={alvoStats} color={ALVO_COLOR} opLabel="registrazioni" />
      <UscitiInfoBox usciti={rows.filter(isFuori)} testid="alvo-usciti-box" />
      {mustUnlock && <p className="text-sm text-amber-600 mb-4">⚠️ Sblocca con la Firma Rapida per registrare l'alvo.</p>}
      <div className="space-y-2" data-testid="alvo-giorno-list">
        {rows.length === 0 && <p className="text-sm text-muted-foreground">Nessun ospite</p>}
        {(() => {
          const attivi = rows.filter((r) => !isDimesso(r));
          const dimessi = rows.filter(isDimesso);
          const renderRow = (r, num) => {
            const rec = records[r.ospite_id];
            const st = rec?.stato;
            const dim = isDimesso(r);
            const fuori = isFuori(r);
            const fuoriDal = r.fuori_dal || null;
            const fuoriOggi = r.fuori_giorno ?? (fuori && (!fuoriDal || selDate >= fuoriDal));
            const fuoriDalLbl = fuoriDal ? ` dal ${fuoriDal.slice(8, 10)}/${fuoriDal.slice(5, 7)}` : "";
            return (
              <div key={r.ospite_id} className={`gr-card p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 ${fuori ? "bg-amber-50/60 border-amber-200" : ""}`} data-testid={`alvo-row-${r.ospite_id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1 w-full">
                  <span className="shrink-0 grid place-items-center h-6 w-6 rounded-full bg-secondary text-muted-foreground text-[11px] font-bold tabnum">{num != null ? num : "–"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold flex items-center gap-2"><button type="button" onClick={(e) => { e.stopPropagation(); navigate(`/ospiti/${r.ospite_id}?tab=alvo`); }} className="hover:underline text-left" data-testid={`alvo-nome-${r.ospite_id}`}>{r.cognome} {r.nome}</button><EsBadge id={r.ospite_id} /><CorrettoBadge correzioni={rec?.correzioni} testid={`alvo-corretto-${r.ospite_id}`} />{dim && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0">Dimesso</span>}{fuori && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${r.presenza === "ospedale" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`alvo-fuori-badge-${r.ospite_id}`}>{fuoriLabel(r)}{fuoriDalLbl}</span>}</div>
                    <div className="text-xs flex items-center gap-2 flex-wrap mt-0.5">
                      <span className="font-bold" style={{ color: riskColor[r.rischio] }}>rischio {r.rischio}</span>
                      {st && rec.operatore && <span className="text-muted-foreground">· da {rec.operatore}</span>}
                      <ConsBadge rec={rec} testid={`alvo-badge-${r.ospite_id}`} />
                      <span className="sm:hidden"><AiutoBadge rec={rec} /></span>
                    </div>
                    <div className="text-[11px] flex items-center gap-1.5 flex-wrap mt-0.5 sm:hidden" data-testid={`alvo-ultima-${r.ospite_id}`}>
                      {r.ultima_evac ? (
                        <>
                          <span className="text-muted-foreground">Ultima evacuazione: <b className="text-foreground capitalize">{fmtEvac(r.ultima_evac)}</b></span>
                          {r.giorni_senza === 0
                            ? <span className="font-bold text-green-600">· evacuata oggi</span>
                            : <span className={`font-bold ${(r.giorni_senza != null && r.giorni_senza >= (r.soglia || 3)) ? "text-red-600" : "text-amber-600"}`}>· da {r.giorni_senza} {r.giorni_senza === 1 ? "giorno" : "giorni"} non evacua</span>}
                          {r.giorni_fuori > 0 && <span className="font-semibold text-amber-700" data-testid={`alvo-giorni-fuori-${r.ospite_id}`}>· {r.giorni_fuori} {r.giorni_fuori === 1 ? "giorno" : "giorni"} fuori struttura (non contati)</span>}
                        </>
                      ) : (
                        <span className="font-semibold text-red-600">Nessuna evacuazione registrata</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-6 shrink-0" data-testid={`alvo-ultima-desk-${r.ospite_id}`}>
                  <div className="w-44">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Ultima evacuazione</div>
                    <div className="text-xs font-semibold capitalize">{r.ultima_evac ? fmtEvac(r.ultima_evac) : <span className="text-red-600">Nessuna registrata</span>}</div>
                  </div>
                  <div className="w-32">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Da quanti giorni</div>
                    <div className="text-xs font-bold">{!r.ultima_evac ? <span className="text-red-600">—</span> : r.giorni_senza === 0 ? <span className="text-green-600">evacuata oggi</span> : <span className={(r.giorni_senza != null && r.giorni_senza >= (r.soglia || 3)) ? "text-red-600" : "text-amber-600"}>da {r.giorni_senza} {r.giorni_senza === 1 ? "giorno" : "giorni"}</span>}</div>
                    {r.giorni_fuori > 0 && <div className="text-[11px] font-semibold text-amber-700 leading-tight" data-testid={`alvo-giorni-fuori-desk-${r.ospite_id}`}>+ {r.giorni_fuori} {r.giorni_fuori === 1 ? "giorno" : "giorni"} fuori struttura (non contati)</div>}
                  </div>
                  <div className="w-44" data-testid={`alvo-aiuto-col-${r.ospite_id}`}>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Aiuto sanitario</div>
                    {rec?.aiuto ? (
                      <>
                        <div className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1"><Cross size={11} /> {aiutoInfo(rec.aiuto)?.l || rec.aiuto}</div>
                        {rec.aiuto_operatore && <div className="text-[10px] text-muted-foreground">✍️ {rec.aiuto_operatore}</div>}
                      </>
                    ) : <div className="text-xs text-muted-foreground">—</div>}
                    {r.ultimo_aiuto && (
                      <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight" data-testid={`alvo-aiuto-ultimo-${r.ospite_id}`}>
                        Ultimo: <b className="text-foreground font-semibold">{aiutoInfo(r.ultimo_aiuto.tipo)?.l || r.ultimo_aiuto.tipo}</b> · <span className="capitalize">{fmtRel(r.ultimo_aiuto.data)}</span>{r.ultimo_aiuto.giorni > 0 ? ` · da ${r.ultimo_aiuto.giorni} ${r.ultimo_aiuto.giorni === 1 ? "giorno" : "giorni"}` : ""}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 w-full sm:w-auto">
                  <button onClick={() => onCell(r, "si")} disabled={fuoriOggi}
                    className={`h-11 flex-1 sm:flex-none px-4 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : st === "si" ? "bg-green-600 text-white" : "bg-secondary hover:bg-green-100"}`}
                    data-testid={`alvo-si-${r.ospite_id}`}><Check size={16} /> Sì</button>
                  <button onClick={() => onCell(r, "no")} disabled={fuoriOggi}
                    className={`h-11 flex-1 sm:flex-none px-4 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : st !== "si" ? "bg-red-600 text-white" : "bg-secondary hover:bg-red-100"}`}
                    data-testid={`alvo-no-${r.ospite_id}`}><X size={16} /> No</button>
                  <button onClick={() => openAiuto(r)} disabled={fuoriOggi} title="Aiuto sanitario"
                    className={`h-11 w-14 shrink-0 rounded-lg flex flex-col items-center justify-center leading-none transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : rec?.aiuto ? "bg-emerald-600 text-white" : "bg-secondary hover:bg-emerald-100 text-emerald-700"}`}
                    data-testid={`alvo-aiuto-btn-${r.ospite_id}`}>
                    <Cross size={16} fill={rec?.aiuto ? "#fff" : "none"} />
                    <span className="text-[7px] font-bold mt-0.5">Aiuto Sanitario</span>
                  </button>
                  <button onClick={() => openInfo(r)} disabled={fuoriOggi} title="Aggiungi dettagli evacuazione"
                    className={`h-11 w-14 shrink-0 rounded-lg flex flex-col items-center justify-center leading-none transition-colors ${fuoriOggi ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-secondary hover:bg-violet-100"}`}
                    style={fuoriOggi ? {} : { color: ALVO_COLOR }}
                    data-testid={`alvo-note-${r.ospite_id}`}>
                    <Plus size={16} />
                    <span className="text-[7px] font-bold mt-0.5">Aggiungi dettagli</span>
                  </button>
                  <SegnalazioneButton categoria="alvo" structureId={selectedStructure} data={selDate}
                    ospiteId={r.ospite_id} ospiteNome={`${r.cognome} ${r.nome}`}
                    entry={segByKey[`${r.ospite_id}_${selDate}`]} onChanged={segRefetch} size={18} />
                </div>
              </div>
            );
          };
          return (
            <>
              {attivi.map((r, i) => renderRow(r, i + 1))}
              {dimessi.length > 0 && (
                <div data-testid="alvo-dimessi-section">
                  <button onClick={() => setShowDimessi((v) => !v)} data-testid="alvo-toggle-dimessi" className="w-full flex items-center gap-2 text-left px-3 py-2.5 rounded-lg bg-orange-50 border border-orange-200 mt-2">
                    <ChevronDown size={16} className={`text-orange-700 transition-transform ${showDimessi ? "" : "-rotate-90"}`} />
                    <span className="font-bold text-sm text-orange-800">Dimessi</span>
                    <span className="text-xs font-semibold text-orange-700 bg-orange-200 rounded-full px-2 py-0.5 tabnum">{dimessi.length}</span>
                  </button>
                  {showDimessi && <div className="space-y-2 mt-2">{dimessi.map((r) => renderRow(r, null))}</div>}
                </div>
              )}
            </>
          );
        })()}
      </div>
      <p className="text-xs text-muted-foreground mt-3">Di default tutti risultano <b>No</b> (non andati in bagno): l'operatore deve toccare <b>Sì</b> solo per chi ha avuto un'evacuazione. Tocca <FileText size={11} className="inline" /> per vedere/aggiungere consistenza (es. diarrea), quantità e note. Dopo 3 giorni senza evacuazione viene generata un'allerta automatica.</p>
      <DettagliDialog ctx={dett} onClose={() => setDett(null)} onSave={saveDett} />
      <AiutoDialog ctx={aiuto} onClose={() => setAiuto(null)} onSave={saveAiuto} />
      <AlvoInfoDialog ctx={info} onClose={() => setInfo(null)} canEdit={infoCanEdit} onEdit={() => { const r = info.r; setInfo(null); openDett(r); }} />
      <ConfirmUndoDialog ctx={confirmUndo} onClose={() => setConfirmUndo(null)} />
    </MeseGiorniEsplosi>
  );
}

function AlvoGridEmbedded({ ospiteId, selectedStructure, today, setCell }) {
  const navigate = useNavigate();
  const { canSign, signerName } = useFirma();
  const [dett, setDett] = useState(null);
  const [info, setInfo] = useState(null);
  const [confirmUndo, setConfirmUndo] = useState(null);
  const guard = (r, d, stato) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    const rec = records[`${r.ospite_id}_${d}`];
    if (rec?.stato === stato) { setConfirmUndo({ nome: `${r.cognome} ${r.nome}`, extra: stato === "si" ? "evacuazione Sì" : "evacuazione No", action: () => setCell(r.ospite_id, r.structure_id, d, "", signerName) }); return; }
    setCell(r.ospite_id, r.structure_id, d, stato, signerName);
  };
  const openDett = (r, d) => {
    if (!canSign) { toast.error("Sblocca con la Firma Rapida (PIN) per registrare"); return; }
    const rec = records[`${r.ospite_id}_${d}`];
    setDett({ ospite_id: r.ospite_id, structure_id: r.structure_id, data: d, nome: `${r.cognome} ${r.nome}`, rec });
  };
  const openInfo = (r, d) => setInfo({ r, d, rec: records[`${r.ospite_id}_${d}`], data: d, nome: `${r.cognome} ${r.nome}` });
  const infoCanEdit = info && canSign;
  const saveDett = async (vals) => {
    await setCell(dett.ospite_id, dett.structure_id, dett.data, vals.stato, signerName, { consistenza: vals.consistenza, quantita: vals.quantita, note: vals.note });
    setDett(null);
    toast.success("Dettagli salvati");
  };
  const curMese = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`; };
  const [mese, setMese] = useState(curMese());
  const shiftMese = (n) => { const [y, m] = mese.split("-").map(Number); const d = new Date(y, m - 1 + n, 1); setMese(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); };
  const meseLbl = (() => { const MM = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"]; const [y, m] = mese.split("-").map(Number); return `${MM[m - 1]} ${y}`; })();
  const yesterday = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return ymd(d); })();
  const { data } = useQuery({
    queryKey: ["alvo", selectedStructure, mese],
    queryFn: async () => (await api.get("/alvo/grid", { params: { structure_id: selectedStructure, mese } })).data,
  });
  const rows = (data?.rows || []).filter((r) => !ospiteId || r.ospite_id === ospiteId);
  const days = data?.days || [];
  const records = data?.records || {};
  const { byKey: segByKey, refetch: segRefetch } = useSegnalazioni("alvo", selectedStructure);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="text-sm font-semibold text-muted-foreground">Registro alvo intestinale</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shiftMese(-1)} data-testid="alvo-prev"><ChevronLeft size={16} /></Button>
          <button onClick={() => setMese(curMese())} className="min-w-[130px] text-center text-sm font-semibold px-2 py-1 rounded-md hover:bg-secondary transition-colors" title="Mese corrente" data-testid="alvo-mese">{meseLbl}</button>
          <Button variant="outline" size="sm" disabled={mese >= curMese()} onClick={() => shiftMese(1)} data-testid="alvo-next"><ChevronRight size={16} /></Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="gr-card p-8 text-center text-muted-foreground text-sm">Nessun ospite</div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.ospite_id} data-testid={`alvo-row-${r.ospite_id}`}>
              {rows.length > 1 && (
                <div className="mb-2 font-semibold flex items-center gap-2"><button type="button" onClick={() => navigate(`/ospiti/${r.ospite_id}?tab=alvo`)} className="hover:underline text-left">{r.cognome} {r.nome}</button><EsBadge id={r.ospite_id} /><span className="text-xs font-bold" style={{ color: riskColor[r.rischio] }}>rischio {r.rischio}</span></div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {days.map((d) => {
                  const rec = records[`${r.ospite_id}_${d}`];
                  const st = rec?.stato;
                  const alert = isAlert(rec?.consistenza);
                  const isToday = d === today;
                  return (
                    <div key={d} className={`rounded-lg border p-2 ${isToday ? "border-primary bg-blue-50/40" : "bg-white"} ${alert ? "ring-1 ring-red-300" : ""}`} data-testid={`alvo-daycell-${r.ospite_id}-${d}`}>
                      <div className={`text-[11px] font-semibold mb-1.5 flex items-center justify-between ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                        <span className="tabnum">{DOW[new Date(d).getDay()]} {fmtDate(d).slice(0, 5)}</span>
                        <CorrettoBadge correzioni={rec?.correzioni} testid={`alvo-corretto-${r.ospite_id}-${d}`} />
                        {isToday ? <span className="text-[9px] font-bold">Oggi</span> : d === yesterday ? <span className="text-[9px]">Ieri</span> : null}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => guard(r, d, "si")} className={`relative h-9 flex-1 rounded-md grid place-items-center transition-colors ${st === "si" ? "bg-green-600 text-white" : "bg-secondary hover:bg-green-100"}`} title={rec?.consistenza ? consInfo(rec.consistenza)?.l : ""} data-testid={`alvo-si-${r.ospite_id}-${d}`}><Check size={16} />{alert && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-600 border border-white" />}</button>
                        <button onClick={() => guard(r, d, "no")} className={`h-9 flex-1 rounded-md grid place-items-center transition-colors ${(st === "no" || (!st && d <= today)) ? "bg-red-600 text-white" : "bg-secondary hover:bg-red-100"}`} data-testid={`alvo-no-${r.ospite_id}-${d}`}><X size={16} /></button>
                        <button onClick={() => openInfo(r, d)} title="Dettagli e note" className={`h-9 w-9 rounded-md grid place-items-center transition-colors bg-secondary hover:bg-violet-100 ${rec?.note || rec?.quantita || rec?.consistenza ? "text-violet-700" : "text-muted-foreground"}`} data-testid={`alvo-dett-${r.ospite_id}-${d}`}><FileText size={14} /></button>
                        <SegnalazioneButton categoria="alvo" structureId={selectedStructure} data={d} ospiteId={r.ospite_id} ospiteNome={`${r.cognome} ${r.nome}`} entry={segByKey[`${r.ospite_id}_${d}`]} onChanged={segRefetch} size={16} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-3">Di default ogni giorno risulta <b>No</b> (X rossa): l'operatore tocca la spunta verde solo quando c'è stata un'evacuazione · <FileText size={11} className="inline" /> per vedere/aggiungere consistenza (es. diarrea), quantità e note. Dopo 3 giorni senza evacuazione viene generata un'allerta automatica.</p>
      <DettagliDialog ctx={dett} onClose={() => setDett(null)} onSave={saveDett} />
      <AlvoInfoDialog ctx={info} onClose={() => setInfo(null)} canEdit={infoCanEdit} onEdit={() => { const { r, d } = info; setInfo(null); openDett(r, d); }} />
      <ConfirmUndoDialog ctx={confirmUndo} onClose={() => setConfirmUndo(null)} />
    </div>
  );
}
